import { ValidationErrors } from "final-form";
import { Descendant } from "slate";

import { parseRichTextFormatToHtml } from "helpers/tickets";
import {
  IContactForm,
  IFormBlockCheckbox,
  IFormBlockConsent,
  IFormBlockEmail,
  IFormBlockHeader,
  IFormBlockInput,
  IFormBlockName,
  IFormBlockOptions,
  IFormBlockParagraph,
  IFormBlockRadio,
  IFormBlockSelect,
  IFormBlockSubject,
  IFormBlockSubmit,
  IFormBlockTextarea,
} from "store/entities/contact-forms/interfaces";
import { compose, maxLength, required, requiredRichText } from "utils/validators";

import { stripHtml } from "../helpers";

type RichTextError = { html: string | undefined };
type OptionsError = Array<string | undefined | { value: string | undefined }>;
type Error = string | RichTextError | OptionsError | undefined;

const MAX_SUBMIT_LENGTH = 40;
const MAX_ERROR_LENGTH = 200;
const MAX_LABEL_LENGTH = 80;
const MAX_OPTION_LENGTH = 100;

const getMaxLengthErrorMessage = (maxLength: number): string => `Enter a max. of ${maxLength} characters`;
export const validate = (values: IContactForm): ValidationErrors => {
  const errors: { fields: Record<string, Error>[]; name: Error } = {
    fields: Array(values?.fields?.length).fill({}),
    name: undefined,
  };
  errors.name = validateName(values?.name);

  values?.fields?.forEach((block, index) => {
    switch (block.type) {
      case "email":
        errors.fields[index] = validateEmailBlock(block);
        break;
      case "input":
      case "name":
        errors.fields[index] = validateInputBlock(block);
        break;
      case "paragraph":
        errors.fields[index] = validateParagraphBlock(block);
        break;
      case "consent":
        errors.fields[index] = validateConsentBlock(block);
        break;
      case "select":
      case "radio":
      case "checkbox":
        errors.fields[index] = validateSelectionBlock(block);
        break;
      case "textarea":
        errors.fields[index] = validateTextareaBlock(block);
        break;
      case "submit":
        errors.fields[index] = validateSubmitBlock(block);
        break;
      case "options":
        errors.fields[index] = validateOptionsBlock(block);
        break;
      case "header":
        errors.fields[index] = validateHeaderBlock(block);
        break;
      case "subject":
        errors.fields[index] = validateSubjectBlock(block);
        break;
    }
  });

  return errors;
};

const validateName = (name: string): Error => {
  return compose(required("Enter contact form name"), maxLength("Length exceeded (limit 200)", 200))(name);
};

const validateEmailBlock = (block: IFormBlockEmail): Record<string, Error> => {
  return {
    value: compose(
      required("Enter email address label"),
      maxLength(getMaxLengthErrorMessage(MAX_LABEL_LENGTH), MAX_LABEL_LENGTH)
    )(block.value),
    requiredError: compose(
      required("Enter error message content"),
      maxLength(getMaxLengthErrorMessage(MAX_ERROR_LENGTH), MAX_ERROR_LENGTH)
    )(block.requiredError),
    incorrectError: compose(
      required("Enter error message content"),
      maxLength(getMaxLengthErrorMessage(MAX_ERROR_LENGTH), MAX_ERROR_LENGTH)
    )(block.incorrectError),
  };
};

const validateInputBlock = (block: IFormBlockInput | IFormBlockName): Record<string, Error> => {
  return {
    value: compose(
      required(`Enter ${block.type === "name" ? "name" : "text"} input label`),
      maxLength(getMaxLengthErrorMessage(MAX_LABEL_LENGTH), MAX_LABEL_LENGTH)
    )(block.value),
    requiredError: compose(
      required("Enter error message content"),
      maxLength(getMaxLengthErrorMessage(MAX_ERROR_LENGTH), MAX_ERROR_LENGTH)
    )(block.requiredError),
  };
};

const htmlFromRichTextMaxLength =
  () =>
  (value: Descendant[]): string | undefined => {
    return maxLength(
      getMaxLengthErrorMessage(MAX_ERROR_LENGTH),
      MAX_ERROR_LENGTH
    )(stripHtml(parseRichTextFormatToHtml(value)));
  };

const validateParagraphBlock = (block: IFormBlockParagraph): Record<string, Error> => {
  return {
    value: {
      html: compose(requiredRichText("Enter description content"), htmlFromRichTextMaxLength())(block.value.html),
    },
  };
};

const validateConsentBlock = (block: IFormBlockConsent): Record<string, Error> => {
  return {
    value: {
      html: requiredRichText("Enter privacy consent")(block.value.html),
    },
    requiredError: compose(
      required("Enter error message"),
      maxLength(getMaxLengthErrorMessage(MAX_ERROR_LENGTH), MAX_ERROR_LENGTH)
    )(block.requiredError),
  };
};

function validateOptions(
  msg = "Enter your choice"
): (value: { value: string }[]) => Array<{ value: string | undefined } | undefined> {
  return (value: { value: string }[]) => {
    return value.map(option => {
      const validationResult = compose(
        required(msg),
        maxLength(getMaxLengthErrorMessage(MAX_OPTION_LENGTH), MAX_OPTION_LENGTH)
      )(option.value ?? "");
      if (validationResult) {
        return { value: validationResult };
      }
    });
  };
}

const validateSelectionBlock = (
  block: IFormBlockRadio | IFormBlockCheckbox | IFormBlockSelect
): Record<string, Error> => {
  const requiredNameError = {
    select: "Enter dropdown list name",
    radio: "Enter single-choice list name",
    checkbox: "Enter multiple-choice list name",
  }[block.type];

  return {
    value: compose(
      required(requiredNameError),
      maxLength(getMaxLengthErrorMessage(MAX_OPTION_LENGTH), MAX_OPTION_LENGTH)
    )(block.value),
    options: validateOptions()(block.options),
    requiredError: compose(
      required("Enter error message"),
      maxLength(getMaxLengthErrorMessage(MAX_ERROR_LENGTH), MAX_ERROR_LENGTH)
    )(block.requiredError),
  };
};

const validateTextareaBlock = (block: IFormBlockTextarea): Record<string, Error> => {
  return {
    value: compose(
      required("Enter message field label"),
      maxLength(getMaxLengthErrorMessage(MAX_LABEL_LENGTH), MAX_LABEL_LENGTH)
    )(block.value),
    requiredError: compose(
      required("Enter error message content"),
      maxLength(getMaxLengthErrorMessage(MAX_ERROR_LENGTH), MAX_ERROR_LENGTH)
    )(block.requiredError),
  };
};

const validateSubmitBlock = (block: IFormBlockSubmit): Record<string, Error> => {
  return {
    value: compose(
      required("Enter submit button label"),
      maxLength(getMaxLengthErrorMessage(MAX_SUBMIT_LENGTH), MAX_SUBMIT_LENGTH)
    )(block.value),
    sendSuccessLabel: compose(
      required("Enter success message"),
      maxLength(getMaxLengthErrorMessage(MAX_LABEL_LENGTH), MAX_LABEL_LENGTH)
    )(block.sendSuccessLabel),
    sendErrorLabel: compose(
      required("Enter error message"),
      maxLength(getMaxLengthErrorMessage(MAX_LABEL_LENGTH), MAX_LABEL_LENGTH)
    )(block.sendErrorLabel),
  };
};

const validateOptionsBlock = (block: IFormBlockOptions): Record<string, Error> => {
  return {
    sendErrorMessage: compose(
      required("Enter error message if sending failed"),
      maxLength(getMaxLengthErrorMessage(MAX_ERROR_LENGTH), MAX_ERROR_LENGTH)
    )(block.sendErrorMessage),
    confirmation: compose(
      required("Enter confirmation message after sending the form"),
      maxLength(getMaxLengthErrorMessage(MAX_ERROR_LENGTH), MAX_ERROR_LENGTH)
    )(block.confirmation),
  };
};

const validateHeaderBlock = (block: IFormBlockHeader): Record<string, Error> => {
  return {
    value: compose(
      required("Enter header content"),
      maxLength(getMaxLengthErrorMessage(MAX_LABEL_LENGTH), MAX_LABEL_LENGTH)
    )(block.value),
  };
};

const validateSubjectBlock = (block: IFormBlockSubject): Record<string, Error> => {
  return {
    value: compose(
      required("Enter subject label"),
      maxLength(getMaxLengthErrorMessage(MAX_LABEL_LENGTH), MAX_LABEL_LENGTH)
    )(block.value),
    requiredError: compose(
      required("Enter error message content"),
      maxLength(getMaxLengthErrorMessage(MAX_ERROR_LENGTH), MAX_ERROR_LENGTH)
    )(block.requiredError),
  };
};
